import React, { useEffect } from 'react';
import { CreditCard, Loader2, X, ShieldCheck } from 'lucide-react';

/** PhonePe checkout temporarily hidden — Stripe only until UPI path is faster/stable. */
const PHONEPE_CHECKOUT_ENABLED = false;

export default function SubscriptionPaymentModal({
  open,
  onClose,
  target,
  region,
  setRegion,
  provider,
  setProvider,
  pricing,
  billingMode,
  onConfirm,
  busy,
}) {
  useEffect(() => {
    if (open && !PHONEPE_CHECKOUT_ENABLED && provider !== 'stripe' && setProvider) {
      setProvider('stripe');
    }
  }, [open, provider, setProvider]);

  if (!open || !target) return null;

  const isIndia = region === 'in';
  const priceRow = target.planKey ? pricing?.[target.planKey] : null;
  const amountLabel = target.amountLabel
    || (target.planKey
      ? (isIndia ? `₹${Math.round(priceRow?.inr || 0).toLocaleString('en-IN')}` : `$${priceRow?.usd || 0}`)
      : (isIndia ? `₹${Math.round(target.priceInr || 0).toLocaleString('en-IN')}` : `$${target.priceUsd || 0}`));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
      <div className="w-full max-w-lg rounded-2xl border border-white/10 bg-[#0d0d14] p-6 shadow-2xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.24em] text-zinc-500">Complete payment</p>
            <h3 className="mt-2 text-xl font-semibold text-white">{target.title}</h3>
            <p className="mt-1 text-sm text-zinc-400">{target.subtitle}</p>
          </div>
          <button type="button" onClick={onClose} className="text-zinc-500 hover:text-white">
            <X size={20} />
          </button>
        </div>

        <div className="mt-6 rounded-xl border border-white/10 bg-white/[0.03] p-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-zinc-400">Total due today</span>
            <span className="text-2xl font-bold text-emerald-300">{amountLabel}</span>
          </div>
          <p className="mt-2 text-xs text-zinc-500">Billed monthly · cancel anytime</p>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-2">
          {[
            { id: 'us', label: 'USA / Global' },
            { id: 'in', label: 'India' },
          ].map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => {
                setRegion(item.id);
                if (!PHONEPE_CHECKOUT_ENABLED) setProvider('stripe');
              }}
              className={`rounded-xl border px-3 py-2 text-sm ${region === item.id ? 'border-emerald-400/50 bg-emerald-400/10 text-white' : 'border-white/10 text-zinc-400'}`}
            >
              {item.label}
            </button>
          ))}
        </div>

        <div className="mt-4 space-y-2">
          <button
            type="button"
            onClick={() => setProvider('stripe')}
            className={`flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-left ${provider === 'stripe' || !PHONEPE_CHECKOUT_ENABLED ? 'border-sky-400/50 bg-sky-400/10' : 'border-white/10 bg-black/20'}`}
          >
            <CreditCard className="text-sky-300" size={18} />
            <div>
              <p className="text-sm font-medium text-white">Credit / Debit Card</p>
              <p className="text-xs text-zinc-500">Secure checkout via Stripe</p>
            </div>
          </button>
          {PHONEPE_CHECKOUT_ENABLED && isIndia && (
            <button
              type="button"
              onClick={() => setProvider('phonepe')}
              className={`flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-left ${provider === 'phonepe' ? 'border-violet-400/50 bg-violet-400/10' : 'border-white/10 bg-black/20'}`}
            >
              <CreditCard className="text-violet-300" size={18} />
              <div>
                <p className="text-sm font-medium text-white">PhonePe</p>
                <p className="text-xs text-zinc-500">UPI, cards & wallets (India)</p>
              </div>
            </button>
          )}
        </div>

        {billingMode === 'mock' && (
          <p className="mt-4 flex items-center gap-2 text-xs text-amber-200/90">
            <ShieldCheck size={14} />
            Mock billing active — you will confirm payment on the next step.
          </p>
        )}

        <button
          type="button"
          onClick={onConfirm}
          disabled={busy}
          className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-400 py-3 text-sm font-semibold text-black hover:bg-emerald-300 disabled:opacity-60"
        >
          {busy ? <Loader2 size={16} className="animate-spin" /> : null}
          Pay {amountLabel} with Stripe
        </button>
      </div>
    </div>
  );
}
